// frontend/src/components/AppSidebar.jsx
/**
 * Drawer de navegación global — DatiaFlow
 *
 * Uso en cada página autenticada:
 *   <AppSidebar user={user} onLogout={handleLogout} />
 *
 * El botón ≡ se monta fixed sobre el header existente (z-[100]).
 * No altera el layout de ninguna página.
 * En mobile: drawer full-width. En desktop: panel de 260px.
 */
// import React, { useState, useEffect, useCallback } from 'react';
import React, { useState, useEffect, useCallback } from 'react';
import { useUIStore } from '../store/useUIStore';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useProjectStore } from '../store/useProjectStore';
import { useLanguageStore } from '../store/useLanguageStore';
import {
  Menu, X, LayoutDashboard, FolderKanban, Layers,
  Users, Building2, LogOut, ChevronRight, ChevronDown,
  HelpCircle, Bell, FileCode2,
} from 'lucide-react';
import Isotipo from '../assets/images/datiaFlow_cuadrado.jpg';

// ── helpers ───────────────────────────────────────────────────────────

const isActive = (pathname, pattern) =>
  pattern === '/'
    ? pathname === pattern
    : pathname.startsWith(pattern);

// ── NavItem ───────────────────────────────────────────────────────────

const NavItem = ({
  to, icon: Icon, label, active, disabled, onClick, children, defaultOpen,
}) => {
  const [open, setOpen] = useState(defaultOpen ?? false);
  const hasChildren = Boolean(children);

  // Si la sección se activa externamente (ej. estamos en /projects/*)
  useEffect(() => {
    if (active && hasChildren) setOpen(true);
  }, [active]);

  const base = `
    w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold
    transition-all duration-150 group
  `;
  const activeStyle = 'bg-indigo-50 text-indigo-700';
  const normalStyle = 'text-slate-600 hover:bg-slate-100 hover:text-slate-900';
  const disabledStyle = 'text-slate-300 cursor-not-allowed';

  const content = (
    <>
      <Icon className={`w-4 h-4 shrink-0 transition-colors
        ${disabled ? 'text-slate-300' : active ? 'text-indigo-600' : 'text-slate-400 group-hover:text-slate-600'}`}
      />
      <span className="flex-1 text-left leading-tight">{label}</span>
      {disabled && (
        <span className="text-[9px] font-bold text-slate-300 bg-slate-100 px-1.5 py-0.5 rounded">
          Soon
        </span>
      )}
      {hasChildren && !disabled && (
        open
          ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
          : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
      )}
    </>
  );

  return (
    <div>
      {disabled ? (
        <div className={`${base} ${disabledStyle}`} title="Próximamente">
          {content}
        </div>
      ) : hasChildren ? (
        <button
          onClick={() => setOpen(o => !o)}
          className={`${base} ${active ? activeStyle : normalStyle}`}
        >
          {content}
        </button>
      ) : (
        <Link
          to={to}
          onClick={onClick}
          className={`${base} ${active ? activeStyle : normalStyle}`}
        >
          {content}
        </Link>
      )}

      {/* Sub-items */}
      {hasChildren && open && (
        <div className="ml-7 mt-1 space-y-0.5 border-l border-slate-200 pl-3">
          {children}
        </div>
      )}
    </div>
  );
};

// ── PhaseSubItem ──────────────────────────────────────────────────────

const PhaseSubItem = ({ phase, projectId, onClose }) => {
  const location = useLocation();
  const active = location.pathname === `/projects/${projectId}` &&
    location.hash === `#phase-${phase.idProjectPhase}`;

  const statusDot = {
    completed: 'bg-emerald-400',
    running: 'bg-amber-400 animate-pulse',
    failed: 'bg-red-400',
    pending: 'bg-slate-300',
    skipped: 'bg-slate-200',
  }[phase.status] ?? 'bg-slate-300';

  return (
    <Link
      to={`/projects/${projectId}#phase-${phase.idProjectPhase}`}
      onClick={onClose}
      className={`flex items-center gap-2 py-1.5 text-xs font-medium rounded-lg px-2
        transition-colors
        ${active ? 'text-indigo-600 bg-indigo-50' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}
    >
      <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${statusDot}`} />
      <span className="truncate">{phase.phaseOrder}. {phase.name}</span>
    </Link>
  );
};

// ── AppSidebar ────────────────────────────────────────────────────────

export const AppSidebar = ({ user, onLogout }) => {
  // const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguageStore();
  const { currentProject } = useProjectStore();
  const { sidebarOpen: open, closeSidebar: close } = useUIStore();

  // const close = useCallback(() => setOpen(false), []);

  // Cerrar con ESC
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, close]);

  // Cerrar al navegar
  useEffect(() => { close(); }, [location.pathname]);

  const inProjects = isActive(location.pathname, '/projects');
  const inDashboard = isActive(location.pathname, '/dashboard');

  const handleLogout = () => {
    close();
    onLogout();
  };

  return (
    <>
      {/* ── Botón hamburger (fixed, siempre visible) ────────────── */}
      {/* <button
        onClick={() => setOpen(true)}
        aria-label="Abrir menú"
        className={`
          fixed top-3.5 left-3 z-[100] p-2 rounded-xl
          bg-white/90 backdrop-blur-sm border border-slate-200
          shadow-sm hover:shadow-md hover:bg-white
          text-slate-600 hover:text-indigo-600
          transition-all duration-150
          ${open ? 'opacity-0 pointer-events-none' : 'opacity-100'}
        `}
      >
        <Menu className="w-5 h-5" />
      </button>
      */}

      {/* ── Backdrop ─────────────────────────────────────────────── */}
      {open && (
        <div
          className="fixed inset-0 z-[110] bg-slate-900/40 backdrop-blur-[2px]"
          onClick={close}
        />
      )}

      {/* ── Panel drawer ─────────────────────────────────────────── */}
      <aside
        className={`
          fixed top-0 left-0 z-[120] h-full w-[260px]
          bg-white border-r border-slate-200 shadow-xl
          flex flex-col
          transition-transform duration-250 ease-in-out
          ${open ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* ── Header del panel ─────────────────────────────────── */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <Link to="/dashboard" onClick={close} className="flex items-center gap-2 group">
            <img
              src={Isotipo}
              alt="DatiaFlow"
              className="h-8 w-8 object-cover object-left rounded-md group-hover:scale-105 transition-transform"
            />
            <span className="text-sm font-black text-slate-800 tracking-tight">DatiaFlow</span>
          </Link>
          <button
            onClick={close}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition"
            aria-label="Cerrar menú"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── Nav principal ─────────────────────────────────────── */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">

          {/* Dashboard */}
          <NavItem
            to="/dashboard"
            icon={LayoutDashboard}
            label={t('dashboard.title') || 'Control ETL'}
            active={inDashboard}
            onClick={close}
          />

          {/* Proyectos */}
          <NavItem
            to="/projects"
            icon={FolderKanban}
            label={t('projects.title') || 'Proyectos'}
            active={inProjects}
            defaultOpen={inProjects}
            onClick={close}
          >
            {/* Sub-item: lista de proyectos */}
            <Link
              to="/projects"
              onClick={close}
              className={`flex items-center gap-2 py-1.5 text-xs font-medium rounded-lg px-2 transition-colors
                ${location.pathname === '/projects'
                  ? 'text-indigo-600 bg-indigo-50'
                  : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}
            >
              <FolderKanban className="w-3 h-3" />
              {t('projects.all') || 'Todos los proyectos'}
            </Link>

            {/* Fases del proyecto activo */}
            {currentProject && (
              <>
                <div className="flex items-center gap-1 px-2 pt-2 pb-1">
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider truncate">
                    {currentProject.name}
                  </span>
                </div>
                {(currentProject.phases || []).map(ph => (
                  <PhaseSubItem
                    key={ph.idProjectPhase}
                    phase={ph}
                    projectId={currentProject.idProject}
                    onClose={close}
                  />
                ))}
                {(currentProject.phases || []).length === 0 && (
                  <p className="text-[10px] text-slate-400 px-2 py-1 italic">
                    {t('projects.phase.empty') || 'Sin fases definidas'}
                  </p>
                )}
              </>
            )}
          </NavItem>

          {/* Separador */}
          <div className="pt-2 pb-1">
            <div className="border-t border-slate-100 mx-1" />
          </div>

          {/* Usuarios / Equipo */}
          <NavItem
            to="/team"
            icon={Users}
            label={t('team.title') || 'Equipo'}
            active={isActive(location.pathname, '/team')}
            onClick={close}
          />

          {/* Cliente */}
          <NavItem
            to="/client"
            icon={Building2}
            label={t('sidebar.client') || 'Cliente'}
            active={isActive(location.pathname, '/client')}
            onClick={close}
          />

          {/* 🔔 Alertas */}
          <NavItem
            to="/alerts"
            icon={Bell}
            label={t('alerting.title') || 'Alertas'}
            active={isActive(location.pathname, '/alerts')}
            onClick={close}
          />
          {/* 🛠️ Script Generator (solo SuperAdmin) */}
          {/* {Number(user?.role) === 1 && (
            <NavItem
              to="/scripts"
              icon={FileCode2}
              label={t('script_generator.nav_label') || 'Scripts'}
              active={isActive(location.pathname, '/scripts')}
              onClick={close}
            />
          )} */}
          {user?.scriptVisible && (
            <NavItem
              to="/scripts"
              icon={FileCode2}
              label={t('script_generator.nav_label') || 'Scripts'}
              active={isActive(location.pathname, '/scripts')}
              onClick={close}
            />
          )}

          {/* Separador */}
          <div className="pt-2 pb-1">
            <div className="border-t border-slate-100 mx-1" />
          </div>

          {/* Ayuda */}
          <NavItem
            to="/help"
            icon={HelpCircle}
            label={t('sidebar.help') || 'Ayuda'}
            active={isActive(location.pathname, '/help')}
            onClick={close}
          />
        </nav>

        {/* ── Footer: usuario + logout ──────────────────────────── */}
        <div className="border-t border-slate-100 px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
              <span className="text-xs font-black text-indigo-700">
                {(user?.name || 'U')[0].toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-slate-800 truncate">{user?.name || '—'}</p>
              <p className="text-[10px] text-slate-400 truncate">{user?.email || ''}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition shrink-0"
              title={t('dashboard.logout') || 'Cerrar sesión'}
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default AppSidebar;
